import { useMutation, useQuery } from '@apollo/client';
import get from 'lodash/get';

import { gql } from '../gql-tag';
import {
  OperatorSessionQl,
  OperatorSessionModelFilter,
  OperatorSessionModelSortEnum,
} from '../graphql';

export const operatorFieldsFragment = gql(`
  fragment OperatorFields on OperatorQL {
    id
    accountId
    name
    spokenGreetingName
    operatorFaq
    places {
      id
      name
    }
  }
`);

export const operatorSettingsFieldsFragment = gql(`
  fragment OperatorSettingsFields on OperatorSettingsQL {
      id
      operator {
        id
      }
      appointmentAgentSettings {
        id
      }
      voiceProvider
      voiceId
      settings
  }
`);

export const operatorPlaceFieldsFragment = gql(`
  fragment OperatorPlaceFields on OperatorPlaceQL {
    id
    operator {
      id
      name
    }
    place {
      id
      name
    }
    description
    settings
  }
`);

export const operatorChannelFieldsFragment = gql(`
  fragment OperatorChannelFields on OperatorChannelQL {
    id
    operatorId
    channelId
    description
    settings
    channel {
      id
      name
      ... on TwilioChannelQL {
        mdn
      }
      ... on BandwidthChannelQL {
        mdn
      }
    }
    operator {
      id
      name
      places {
        id
        name
      }
    }
  }
`);

export const operatorSessionFieldsFragment = gql(`
  fragment OperatorSessionFields on OperatorSessionQL {
    id
    createdOn
    callSid
    isActive
    startTime
    endTime
    callDuration
    outcome
    slots
    participant {
      id
      address
      displayName
    }
    callRecording {
      id
      url
    }
    phonecall {
      id
      callDuration
      isActive
      endtime
      startTime
      conversationId
      transcriptId
      place {
        id
        name
      }
      returnCallsRoutingOutcome
      returnCallsRoutedTo
      returnCallsRoutedUser {
        id
        displayName
      }
      participant {
        id
        address
        displayName
      }
      callRecording {
        id
        url
      }
    }
  }
`);

export const GET_OPERATORS = gql(`
  query getOperators($accountId: String!) {
    operators(accountId: $accountId) {
      ...OperatorFields
    }
  }
`);

export const useGetOperators = (accountId: string) => {
  const { data, loading, error } = useQuery(GET_OPERATORS, {
    variables: { accountId },
  });

  const operators = get(data, 'operators') ?? [];
  return { operators, loading, error };
};

export const GET_OPERATOR = gql(`
  query getOperator(
    $id: String!
    $includeChannels: Boolean = false
    $includeSessions: Boolean = false
  ) {
    operator(id: $id) {
      ...OperatorFields
      channels @include(if: $includeChannels) {
        id
        name
        ... on TwilioChannelQL {
          mdn
        }
        ... on BandwidthChannelQL {
          mdn
        }
      }
      operatorSessions @include(if: $includeSessions) {
        ...OperatorSessionFields
      }
    }
  }
`);

export const useGetOperatorById = (
  id: string,
  includeChannels: boolean = false,
  includeSessions: boolean = false,
) => {
  const { data, loading, error } = useQuery(GET_OPERATOR, {
    variables: { id, includeChannels, includeSessions },
  });

  const operator = get(data, 'operator');
  return { operator, loading, error };
};

export const CREATE_OPERATOR_MUTATION = gql(`
  mutation createOperator($input: CreateOperatorInput!) {
    createOperator(input: $input) {
      operator {
        ...OperatorFields
      }
    }
  }
`);

export const useCreateOperatorMutation = () => {
  return useMutation(CREATE_OPERATOR_MUTATION, {
    update(cache, { data }) {
      const operator = data?.createOperator?.operator;
      if (operator) {
        cache.updateQuery(
          {
            query: GET_OPERATORS,
            variables: { accountId: operator.accountId },
          },
          operators => {
            if (!operators?.operators || operators.operators.length === 0) {
              return {
                operators: [operator],
              };
            }

            return {
              operators: [...operators.operators, operator],
            };
          },
        );
      }
    },
  });
};

export type ExternalPlace = {
  id: string;
  name: string;
  description: string;
  mdn: string;
};

export type OperatorSettings = {
  has_appointment_booking: boolean;
  external_places: ExternalPlace[];
  human_escalation_phone_number?: string;
};

export type OperatorChannelSettings = {
  fallback_enabled?: boolean;
  fallback_place_id?: string;
  fallback_phoneline_id?: string;
};

export type OperatorDealershipInfoQl = {
  name: string;
  spoken_greeting_name: string | null;
  faq: string | null;
  places: {
    place_id: string;
    name: string;
    internal_name: string;
    inbox_type: string;
    description: string;
    name_pronunciation: string | null;
    hours: {
      [key: string]: string;
    };
  }[];
  users: {
    user_id: string;
    places: {
      place_id: string;
      name: string;
      internal_name: string;
    }[];
    teams: {
      name: string;
    }[];
    can_transfer_to: boolean;
    can_leave_voicemail: boolean;
    name: string;
    first_name: string | null;
    last_name: string | null;
    name_pronunciation: string | null;
    email: string;
    job_role: string | null;
    schedule: {
      [key: string]: string;
    };
  }[];
  has_appointment_booking: boolean;
  employee_directory: {
    departments: {
      place_id: string;
      name: string;
      internal_name: string;
      inbox_type: string;
      description: string;
      name_pronunciation: string | null;
      hours: {
        [key: string]: string;
      };
    }[];
    employees: {
      user_id: string;
      places: { place_id: string; name: string }[];
      has_phoneline: boolean;
      name: string;
      first_name: string;
      last_name: string;
      name_pronunciation: string | null;
      email: string;
      job_role: string | null;
      teams: string[];
      schedule: {
        [key: string]: string;
      };
    }[];
  };
  emergency_contacts: {
    security: string;
    roadside_assistance: string;
  };
};

export const GET_OPERATOR_WITH_DEALERSHIP_INFO = gql(`
  query getOperatorWithDealershipInfo($id: String!) {
    operator(id: $id) {
      id
      name
      places {
        id
        name
      }
      dealershipInfoBlob
    }
  }
`);

export const useGetOperatorDealershipInfoById = (id: string) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_WITH_DEALERSHIP_INFO, {
    variables: { id },
  });

  if (loading || error || !data?.operator) {
    return { dealershipInfo: null, loading, error };
  }

  const dealershipInfo = JSON.parse(
    data.operator.dealershipInfoBlob,
  ) as OperatorDealershipInfoQl;

  return {
    dealershipInfo,
    loading,
    error,
  };
};

export const GET_OPERATOR_SETTINGS = gql(`
  query getOperatorSettings($operatorId: String!) {
    operatorSettings(operatorId: $operatorId) {
      ...OperatorSettingsFields
    }
  }
`);

export const useGetOperatorSettings = (operatorId: string) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_SETTINGS, {
    variables: { operatorId },
  });

  const operatorSettings = get(data, 'operatorSettings');
  return { operatorSettings, loading, error };
};

export const UPDATE_OPERATOR_SETTINGS = gql(`
  mutation updateOperatorSettings($input: UpdateOperatorSettingsInput!) {
    updateOperatorSettings(input: $input) {
      operatorSettings {
        ...OperatorSettingsFields
      }
    }
  }
`);

export const useUpdateOperatorSettingsMutation = () => {
  return useMutation(UPDATE_OPERATOR_SETTINGS, {
    update(cache, { data }) {
      const operatorSettings = data?.updateOperatorSettings?.operatorSettings;
      if (operatorSettings) {
        cache.writeFragment({
          id: `OperatorSettingsQL:${operatorSettings.id}`,
          fragment: operatorSettingsFieldsFragment,
          data: operatorSettings,
        });
      }
    },
  });
};

export const GET_OPERATOR_PLACES = gql(`
  query getOperatorPlaces($operatorId: String!) {
    operatorPlaces(operatorId: $operatorId) {
      ...OperatorPlaceFields
    }
  }
`);

export const useGetOperatorPlaces = (operatorId: string) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_PLACES, {
    variables: { operatorId },
  });

  const operatorPlaces = get(data, 'operatorPlaces') ?? [];
  return { operatorPlaces, loading, error };
};

export const GET_OPERATOR_PLACE = gql(`
  query getOperatorPlace($operatorId: String!, $placeId: String!) {
    operatorPlace(operatorId: $operatorId, placeId: $placeId) {
      ...OperatorPlaceFields
    }
  }
`);

export const useGetOperatorPlace = (operatorId: string, placeId: string) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_PLACE, {
    variables: { operatorId, placeId },
  });

  const operatorPlace = get(data, 'operatorPlace');
  return { operatorPlace, loading, error };
};

export const GET_OPERATOR_CHANNEL = gql(`
  query getOperatorChannel($operatorId: String!, $channelId: String!) {
    operatorChannel(operatorId: $operatorId, channelId: $channelId) {
      ...OperatorChannelFields
    }
  }
`);

export const useGetOperatorChannel = (
  operatorId: string,
  channelId: string,
) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_CHANNEL, {
    variables: { operatorId, channelId },
  });

  const operatorChannel = get(data, 'operatorChannel');
  return { operatorChannel, loading, error };
};

export const ADD_OPERATOR_PLACE_MUTATION = gql(`
  mutation addOperatorPlace($input: AddOperatorPlaceInput!) {
    addOperatorPlace(input: $input) {
      operator {
        ...OperatorFields
      }
      operatorPlace {
        ...OperatorPlaceFields
      }
    }
  }
`);

export const useAddOperatorPlaceMutation = () => {
  return useMutation(ADD_OPERATOR_PLACE_MUTATION, {
    update(cache, { data }) {
      const operatorPlace = data?.addOperatorPlace?.operatorPlace;
      const operatorId = operatorPlace?.operator?.id;

      if (operatorPlace && operatorId) {
        cache.updateQuery(
          {
            query: GET_OPERATOR_PLACES,
            variables: { operatorId },
          },
          existingData => {
            if (
              !existingData?.operatorPlaces ||
              existingData.operatorPlaces.length === 0
            ) {
              return {
                operatorPlaces: [operatorPlace],
              };
            }

            return {
              operatorPlaces: [...existingData.operatorPlaces, operatorPlace],
            };
          },
        );
      }
    },
  });
};

export const UPDATE_OPERATOR_PLACE_DESCRIPTION = gql(`
  mutation updateOperatorPlaceDescription($input: UpdateOperatorPlaceDescriptionInput!) {
    updateOperatorPlaceDescription(input: $input) {
      operatorPlace {
        ...OperatorPlaceFields
      }
    }
  }
`);

export const useUpdateOperatorPlaceDescriptionMutation = () => {
  return useMutation(UPDATE_OPERATOR_PLACE_DESCRIPTION);
};

export const UPDATE_OPERATOR_PLACE_SETTINGS = gql(`
  mutation updateOperatorPlaceSettings($input: UpdateOperatorPlaceSettingsInput!) {
    updateOperatorPlaceSettings(input: $input) {
      operatorPlace {
        ...OperatorPlaceFields
      }
    }
  }
`);

export const useUpdateOperatorPlaceSettingsMutation = () => {
  return useMutation(UPDATE_OPERATOR_PLACE_SETTINGS);
};

export const UPDATE_OPERATOR_CHANNEL = gql(`
  mutation updateOperatorChannel($input: UpdateOperatorChannelInput!) {
    updateOperatorChannel(input: $input) {
      operator {
        ...OperatorFields
      }
      operatorChannel {
        ...OperatorChannelFields
      }
    }
  }
`);

export const useUpdateOperatorChannelMutation = () => {
  return useMutation(UPDATE_OPERATOR_CHANNEL);
};

export const ADD_OPERATOR_CHANNEL_MUTATION = gql(`
  mutation addOperatorChannel($input: AddOperatorChannelInput!) {
    addOperatorChannel(input: $input) {
      operator {
        ...OperatorFields
      }
      operatorChannel {
        ...OperatorChannelFields
      }
    }
  }
`);

export const useAddOperatorChannelMutation = () => {
  return useMutation(ADD_OPERATOR_CHANNEL_MUTATION, {
    update(cache, { data }) {
      const operatorChannel = data?.addOperatorChannel?.operatorChannel;
      const operator = data?.addOperatorChannel?.operator;

      if (operatorChannel && operator) {
        // Update the operator to include the new channel
        cache.updateQuery(
          {
            query: GET_OPERATOR,
            variables: {
              id: operator.id,
              includeChannels: true,
              includeSessions: false,
            },
          },
          existingData => {
            if (!existingData?.operator) {
              return existingData;
            }

            const existingChannels = existingData.operator.channels || [];

            return {
              ...existingData,
              operator: {
                ...existingData.operator,
                channels: [...existingChannels, operatorChannel.channel],
              },
            };
          },
        );
      }
    },
  });
};

export const GET_OPERATOR_SESSIONS = gql(`
  query getOperatorSessions(
    $operatorId: String!
    $userIds: [String!]
    $placeIds: [String!]
    $first: Int
    $after: String
    $filter: OperatorSessionModelFilter
    $sort: [OperatorSessionModelSortEnum]
  ) {
    operatorSessions(
      operatorId: $operatorId
      userIds: $userIds
      placeIds: $placeIds
      first: $first
      after: $after
      queryFilter: $filter
      sort: $sort) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...OperatorSessionFields
        }
      }
      totalCount
    }
  }
`);

export const useGetOperatorSessions = ({
  operatorId,
  placeIds = [],
  userIds = [],
  first,
  after,
  filter = {},
  sort = [OperatorSessionModelSortEnum.CreatedOnDesc],
}: {
  operatorId: string;
  placeIds?: string[];
  userIds?: string[];
  first?: number;
  after?: string | null;
  filter?: OperatorSessionModelFilter;
  sort?: OperatorSessionModelSortEnum[];
}) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_SESSIONS, {
    variables: {
      operatorId,
      placeIds,
      userIds,
      filter,
      first,
      after,
      sort,
    },
  });

  const totalCount = get(data, 'operatorSessions.totalCount');
  const endCursor = get(data, 'operatorSessions.pageInfo.endCursor');
  const hasNextPage = get(data, 'operatorSessions.pageInfo.hasNextPage');
  const operatorSessions = (get(data, 'operatorSessions.edges') ?? [])
    .map(edge => {
      if (!edge || !edge.node) {
        return null;
      }

      return edge.node as OperatorSessionQl;
    })
    .filter((node): node is OperatorSessionQl => node !== null);

  return {
    operatorSessions,
    loading,
    error,
    totalCount,
    endCursor,
    hasNextPage,
  };
};

export const GET_OPERATOR_SESSION = gql(`
  query getOperatorSession($id: String!, $includeTranscript: Boolean = false) {
    operatorSession(id: $id) {
      ...OperatorSessionFields
      operatorTranscript @include(if: $includeTranscript) {
        id
        events {
          id
          type
          timestamp
          start
          end
          text
          confidence
          speaker
        }
      }
    }
  }
`);

export const useGetOperatorSessionById = (
  id: string,
  includeTranscript: boolean = false,
) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_SESSION, {
    variables: { id, includeTranscript },
  });

  const operatorSession = get(data, 'operatorSession');
  return { operatorSession, loading, error };
};

export const UPDATE_OPERATOR_MUTATION = gql(`
  mutation updateOperator($input: UpdateOperatorInput!) {
    updateOperator(input: $input) {
      operator {
        ...OperatorFields
      }
    }
  }
`);

export const useUpdateOperatorMutation = () => {
  return useMutation(UPDATE_OPERATOR_MUTATION, {
    update(cache, { data }) {
      const operator = data?.updateOperator?.operator;
      if (operator) {
        cache.writeFragment({
          id: `OperatorQL:${operator.id}`,
          fragment: operatorFieldsFragment,
          data: operator,
        });
      }
    },
  });
};

export const GET_PLACES_USERS_FOR_OPERATOR = gql(`
  query placesUsersForOperator($placeId: String!) {
    placesUsers(placeId: $placeId) {
      id
      placeId
      role
      user {
        id
        displayName
        jobRole
        userPhones {
          id
        }
        phonelines {
          id
          place {
            id
          }
        }
      }
    }
  }
`);

export const useGetPlacesUsersForOperator = (placeId: string) => {
  const { data, loading, error } = useQuery(GET_PLACES_USERS_FOR_OPERATOR, {
    variables: { placeId },
  });

  const placesUsers = get(data, 'placesUsers');
  return { placesUsers, loading, error };
};
